import { Injectable, signal, computed, inject } from '@angular/core';
import {
  UserInfo,
  LoginCredentials,
  Address,
  ShareUser,
  ShipmentAddress,
  UserPreferences
} from '../models';
import { AppStateService } from './app-state.service';

@Injectable({
  providedIn: 'root'
})
export class UserStateService {
  private readonly appState = inject(AppStateService);
  // Signals for reactive state management
  private readonly _user = signal<UserInfo | null>(null);
  private readonly _originalUser = signal<UserInfo | null>(null);  // Parked admin user during support session
  private readonly _parkedJwtToken = signal<string | null>(null);  // Parked admin JWT during support session
  private readonly _parkedRefreshToken = signal<string | null>(null);
  private readonly _isSupportSession = signal<boolean>(false);  // Track if in support session
  private readonly _addresses = signal<ShipmentAddress[]>([]);
  private readonly _balance = signal<any[]>([]);
  private readonly _projects = signal<any[]>([]);
  private readonly _projectsBin = signal<any[]>([]);
  private readonly _orders = signal<any[]>([]);
  private readonly _ordersDrafts = signal<any[]>([]);
  private readonly _oldOrders = signal<any[]>([]);
  private readonly _shares = signal<any[]>([]);
  private readonly _sharesNotifications = signal<any[]>([]);
  private readonly _invoices = signal<any[]>([]);
  private readonly _collections = signal<any[]>([]);
  private readonly _localCredentials = signal<LoginCredentials>({ username: '', password: '', trial: 1, update: 0 });
  private readonly _localUser = signal<ShareUser | null>(null);
  private readonly _rememberSelected = signal<boolean>(false);
  private readonly _keepSession = signal<boolean>(false);
  private readonly _avatarUploaded = signal<boolean>(false);
  private readonly _jwtToken = signal<string | null>(null);
  private readonly _refreshToken = signal<string | null>(null);
  private readonly _sessionReady = signal<boolean>(false);

  // Computed values
  readonly user = this._user.asReadonly();
  readonly originalUser = this._originalUser.asReadonly();
  readonly parkedJwtToken = this._parkedJwtToken.asReadonly();
  readonly parkedRefreshToken = this._parkedRefreshToken.asReadonly();
  readonly isSupportSession = this._isSupportSession.asReadonly();
  readonly addresses = this._addresses.asReadonly();
  readonly balance = this._balance.asReadonly();
  readonly projects = this._projects.asReadonly();
  readonly orders = this._orders.asReadonly();
  readonly shares = this._shares.asReadonly();
  readonly localCredentials = this._localCredentials.asReadonly();
  readonly localUser = this._localUser.asReadonly();
  readonly rememberSelected = this._rememberSelected.asReadonly();
  readonly keepSession = this._keepSession.asReadonly();
  readonly jwtToken = this._jwtToken.asReadonly();
  readonly refreshToken = this._refreshToken.asReadonly();
  /** True once checkSession() has finished (success or failure) */
  readonly sessionReady = this._sessionReady.asReadonly();

  readonly isLoggedIn = computed(() => {
    // JWT-first: if we have a JWT token, the user is logged in
    if (this._jwtToken() !== null) return true;
    // Legacy fallback: check user credentials
    const user = this._user();
    return user !== null && user.credentials && user.credentials.trial === 0;
  });

  /**
   * Whether price values may be shown to the current user. The backend sends
   * `showPrice` on the user (0 = this account must not see prices, e.g. a
   * reseller ordering on a client's behalf). Default to visible when the flag
   * is absent (undefined) — only an explicit 0 hides prices. Ordering still
   * works either way; this gates display only.
   */
  readonly pricesVisible = computed(() => Number(this._user()?.showPrice) !== 0);

  readonly newShareNotifications = computed(() => {
    const notifications = this._sharesNotifications();
    return notifications.filter(n => n.read !== 1).length;
  });

  readonly fullName = computed(() => {
    const user = this._user();
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`.trim();
  });

  // Setters
  setUser(user: UserInfo | null): void {
    this._user.set(user);
  }

  setAddresses(addresses: ShipmentAddress[]): void {
    this._addresses.set(addresses);
  }

  setBalance(balance: any[]): void {
    this._balance.set(balance);
  }

  setProjects(projects: any[]): void {
    this._projects.set(projects);
  }

  setProjectsBin(projectsBin: any[]): void {
    this._projectsBin.set(projectsBin);
  }

  setOrders(orders: any[]): void {
    this._orders.set(orders);
  }

  setOrdersDrafts(ordersDrafts: any[]): void {
    this._ordersDrafts.set(ordersDrafts);
  }

  setOldOrders(oldOrders: any[]): void {
    this._oldOrders.set(oldOrders);
  }

  setShares(shares: any[]): void {
    this._shares.set(shares);
  }

  setSharesNotifications(sharesNotifications: any[]): void {
    this._sharesNotifications.set(sharesNotifications);
  }

  setInvoices(invoices: any[]): void {
    this._invoices.set(invoices);
  }

  setCollections(collections: any[]): void {
    this._collections.set(collections);
  }

  setLocalCredentials(credentials: LoginCredentials): void {
    this._localCredentials.set(credentials);
  }

  setLocalUser(user: ShareUser | null): void {
    this._localUser.set(user);
  }

  setRememberSelected(value: boolean): void {
    this._rememberSelected.set(value);
  }

  setKeepSession(value: boolean): void {
    this._keepSession.set(value);
  }

  setAvatarUploaded(value: boolean): void {
    this._avatarUploaded.set(value);
  }

  setTokens(jwt: string, refresh: string): void {
    this._jwtToken.set(jwt);
    this._refreshToken.set(refresh);
  }

  clearTokens(): void {
    this._jwtToken.set(null);
    this._refreshToken.set(null);
  }

  setSessionReady(): void {
    this._sessionReady.set(true);
  }

  clear(): void {
    this.clearTokens();
    this.clearCachedData();
  }

  /**
   * Drop customer-scoped cached data (addresses, projects, orders…) without
   * touching auth tokens. Used when swapping the active user (support session
   * start/end) so the new user's data is fetched fresh while keeping their
   * just-set tokens intact.
   */
  private clearCachedData(): void {
    this._addresses.set([]);
    this._shares.set([]);
    this._sharesNotifications.set([]);
    this._orders.set([]);
    this._ordersDrafts.set([]);
    this._oldOrders.set([]);
    this._projects.set([]);
  }

  /**
   * Start a support session — park the admin's user + tokens, swap to the
   * customer's. Atomic so the admin's tokens never disappear from in-memory
   * state between two operations (the previous flow set the customer tokens
   * then called clear(), which wiped them).
   *
   * Customer-scoped caches (addresses, projects, orders…) are dropped so
   * subsequent fetches load the impersonated customer's data. Tokens are
   * NOT touched by that drop — they're swapped explicitly.
   *
   * Re-entry guard: only parks the admin slot if not already in a support
   * session, so a second startSupportSession call (e.g. impersonating a
   * second customer without exiting first) doesn't overwrite the admin
   * with the previous customer.
   */
  startSupportSession(
    targetUser: UserInfo,
    customerTokens: { access: string; refresh: string }
  ): void {
    if (!this._isSupportSession()) {
      this._originalUser.set(this._user());
      this._parkedJwtToken.set(this._jwtToken());
      this._parkedRefreshToken.set(this._refreshToken());
    }
    this._user.set(targetUser);
    this._jwtToken.set(customerTokens.access);
    this._refreshToken.set(customerTokens.refresh);
    this._isSupportSession.set(true);
    this.clearCachedData();
    console.log('[UserStateService] Support session started as:', targetUser.email);
  }

  /**
   * Rehydrate a support session from persisted state (called by
   * ApiService.restoreTokens on app boot when localStorage holds parked
   * admin tokens). The active tokens are NOT touched here — they're
   * restored separately via setTokens(); this only fills in the parked
   * slots + flips the support-session flag so the banner mounts.
   *
   * Distinct from startSupportSession because we're not swapping the
   * active user/tokens here — just remounting the parked state that was
   * already in effect when the page reloaded.
   */
  restoreParkedSession(
    parkedUser: UserInfo,
    parkedTokens: { access: string; refresh: string }
  ): void {
    this._originalUser.set(parkedUser);
    this._parkedJwtToken.set(parkedTokens.access);
    this._parkedRefreshToken.set(parkedTokens.refresh);
    this._isSupportSession.set(true);
  }

  /**
   * Rotate the parked admin's JWT/refresh after a parked-context
   * refresh-on-401 cycle (a `/support/*` request fired during impersonation
   * came back 401, so ApiService refreshed the *parked* admin token
   * instead of the active customer one). Doesn't touch the support-session
   * flag, the parked user, or the active tokens.
   */
  updateParkedTokens(access: string, refresh: string): void {
    this._parkedJwtToken.set(access);
    this._parkedRefreshToken.set(refresh);
  }

  /**
   * End the support session — restore the parked admin user + tokens.
   * Returns the parked user *and* tokens so the caller can persist the
   * tokens to localStorage (which is the boot-time source of truth).
   */
  endSupportSession(): {
    user: UserInfo;
    tokens: { access: string | null; refresh: string | null };
  } | null {
    const originalUser = this._originalUser();
    if (!originalUser || !this._isSupportSession()) return null;

    const tokens = {
      access: this._parkedJwtToken(),
      refresh: this._parkedRefreshToken()
    };

    this._user.set(originalUser);
    this._jwtToken.set(tokens.access);
    this._refreshToken.set(tokens.refresh);
    this._originalUser.set(null);
    this._parkedJwtToken.set(null);
    this._parkedRefreshToken.set(null);
    this._isSupportSession.set(false);
    this.clearCachedData();
    console.log('[UserStateService] Support session ended, returned to:', originalUser.email);
    return { user: originalUser, tokens };
  }

  updateUserAvatar(avatar: any): void {
    const user = this._user();
    if (user) {
      this._user.set({ ...user, avatar });
      this._avatarUploaded.set(true);
    }
  }

  /**
   * Get credentials from current user or local credentials
   * Used for AMF service calls that require LoginCredentials
   */
  getCredentials(): LoginCredentials | null {
    const user = this._user();
    if (user?.credentials) {
      return user.credentials;
    }
    const localCreds = this._localCredentials();
    if (localCreds.username) {
      return localCreds;
    }
    return null;
  }

  /**
   * Get current locale from AppStateService (UI selection) or user preferences
   */
  getLocale(): string {
    // Check AppStateService first (user's current UI language selection)
    const appLocale = this.appState.locale();
    if (appLocale) {
      return appLocale;
    }
    // Fall back to user preferences
    const user = this._user();
    return user?.preferences?.locale || 'pt_PT';
  }
}
